import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import AccessibilityMenu from './AccessibilityMenu';
import LogoutConfirmModal from './LogoutConfirmModal';
import { startTourForPath } from './TutorialOverlay';
import { clearLoginPrefill, saveLoginPrefill } from '../../utils/loginPrefill';
import { resolveAdminRole } from '../../utils/adminRole';
import './Navbar.css';
import '../../assets/fontello-plus/fontello-73784b09/css/calander.css';
import '../../assets/fontello-change/fontello-79be5077/css/fontello.css';
import logo from '../../assets/finalclipstonedesginlogo-removebg-preview.png';

/**
 * Navbar Component
 * Renders the top navigation bar across the application.
 * Manages responsive menus, dropdowns, and user session state (auth & admin).
 */
const Navbar = () => {
  // Navigation and UI state
  const [isMenuOpen, setIsMenuOpen]           = useState(false);
  const [user, setUser]                       = useState(null);
  const [isAdmin, setIsAdmin]                 = useState(false);
  
  // Dropdown visibility states
  const [accountDropOpen, setAccountDropOpen] = useState(false);
  const [trustDropOpen, setTrustDropOpen]     = useState(false);
  const [socialDropOpen, setSocialDropOpen]   = useState(false);
  const [logoutModalStep, setLogoutModalStep] = useState(null);
  const [logoutSubmitting, setLogoutSubmitting] = useState(false);
  
  const location                              = useLocation();
  const navigate                              = useNavigate();
  const accountDropRef                        = useRef(null);
  const trustDropRef                          = useRef(null);
  const socialDropRef                         = useRef(null);

  // ── Session + admin check ────────────────────────────────────────────────────
  // Loads user session on mount and listens for auth changes. Checks if user is admin.
  useEffect(() => {
    const loadUser = async (session) => {
      if (!session) {
        setUser(null);
        setIsAdmin(false);
        return;
      }

      setUser(session.user);

      // Check if this user is an admin
      try {
        const role = await resolveAdminRole(session.user.email);
        setIsAdmin(role.isAdmin);
      } catch (err) {
        console.error('Navbar admin check error:', err);
        setIsAdmin(false);
      }
    };

    // Get current session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      loadUser(session);
    });

    // Listen for login/logout anywhere in the app
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        loadUser(session);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // ── Close dropdowns when clicking outside ───────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (accountDropRef.current && !accountDropRef.current.contains(e.target)) {
        setAccountDropOpen(false);
      }
      if (trustDropRef.current && !trustDropRef.current.contains(e.target)) {
        setTrustDropOpen(false);
      }
      if (socialDropRef.current && !socialDropRef.current.contains(e.target)) {
        setSocialDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Close everything on route change ────────────────────────────────────────
  useEffect(() => {
    setAccountDropOpen(false);
    setTrustDropOpen(false);
    setSocialDropOpen(false);
    setIsMenuOpen(false);
  }, [location.pathname]);

  // ── Toggle body class for mobile menu scroll locking & hiding bottom quick nav ──
  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    return () => {
      document.body.classList.remove('mobile-menu-open');
    };
  }, [isMenuOpen]);

  // ── Sign out ─────────────────────────────────────────────────────────────────
  const performSignOut = async (shouldSaveDetails) => {
    setLogoutSubmitting(true);

    if (shouldSaveDetails) {
      const savedUser = (() => {
        try {
          const stored = window.localStorage.getItem('user');
          return stored ? JSON.parse(stored) : null;
        } catch {
          return null;
        }
      })();

      saveLoginPrefill({
        email: user?.email,
        username: user?.user_metadata?.username || savedUser?.user_metadata?.username || '',
      });
    } else {
      clearLoginPrefill();
    }

    await supabase.auth.signOut();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('isAdmin');
    setUser(null);
    setIsAdmin(false);
    setAccountDropOpen(false);
    setIsMenuOpen(false);
    setLogoutModalStep(null);
    setLogoutSubmitting(false);
    navigate('/login');
  };

  const handleSignOut = () => {
    setLogoutModalStep('confirm');
  };

  const toggleMenu          = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu           = () => setIsMenuOpen(false);
  const [helpPromptVisible, setHelpPromptVisible] = useState(false);
  const handleHelpClick = () => {
    if (startTourForPath(location.pathname)) {
      setHelpPromptVisible(true);
      window.setTimeout(() => setHelpPromptVisible(false), 2200);
    }
    closeMenu();
  };

  const getNavbarTitle = () => {
    const path = location.pathname;
    if (path === '/')                             return 'Clipstone Miners Welfare';
    if (path === '/trust' || path === '/history') return 'CLIPSTONE MINERS WELFARE - COMMUNITY TRUST';
    if (path === '/social')                       return 'CLIPSTONE SOCIAL CLUB';
    return 'CLIPSTONE MINERS WELFARE';
  };



  const accountTarget = user ? (isAdmin ? '/admin-account' : '/account') : '/login';
  const accountLabel = user ? (isAdmin ? 'Admin' : 'Account') : 'Login';

  return (
    <nav className="navbar">
      <div className="navbar-container">

        {/* Logo + title */}
        <Link id="nav-logo" to="/" className="navbar-brand" style={{ textDecoration: 'none' }}>
          <img src={logo} alt="Clipstone Logo" className="navbar-logo" />
          <span className="navbar-title">{getNavbarTitle()}</span>
          <span className="navbar-title-mobile">Clipstone Miners Welfare</span>
        </Link>

        {/* Mobile controls on the right: Accessibility Menu + 3-line Hamburger */}
        <div className="navbar-mobile-controls">
          <AccessibilityMenu
            isNavDrawerOpen={isMenuOpen}
            onOpen={() => setIsMenuOpen(false)}
          />
          <button
            type="button"
            className={`menu-toggle ${isMenuOpen ? 'open' : ''}`}
            onClick={toggleMenu}
            aria-label="Toggle navigation"
            aria-expanded={isMenuOpen}
          >
            <span className="hamburger-bar"></span>
            <span className="hamburger-bar"></span>
            <span className="hamburger-bar"></span>
          </button>
        </div>

        {/* Nav links (Slide-out drawer on mobile/tablet, full horizontal on PC) */}
        <div className={`navbar-links-wrapper ${isMenuOpen ? 'show' : ''}`}>
          <div className="navbar-actions">
            
            {/* Mobile Drawer Auth Card (Mobile/Tablet only) */}
            <div className="mobile-drawer-auth">
              {user ? (
                <div className="mobile-drawer-user-box">
                  <div className="mobile-drawer-user-info">
                    <span className="mobile-drawer-avatar">
                      {user.email.charAt(0).toUpperCase()}
                    </span>
                    <div className="mobile-drawer-user-details">
                      <span className="mobile-drawer-user-email">{user.email}</span>
                      <span className="mobile-drawer-user-role">{accountLabel}</span>
                    </div>
                  </div>
                  <div className="mobile-drawer-user-actions">
                    <Link
                      to={accountTarget}
                      className="mobile-drawer-btn"
                      onClick={closeMenu}
                    >
                      {isAdmin ? 'Dashboard' : 'My Account'}
                    </Link>
                    <button
                      type="button"
                      className="mobile-drawer-btn mobile-drawer-btn--signout"
                      onClick={handleSignOut}
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mobile-drawer-login-box">
                  <Link
                    id="drawer-login-btn"
                    to="/login"
                    className="mobile-drawer-login-btn"
                    onClick={closeMenu}
                  >
                    Login / Register
                  </Link>
                </div>
              )}
            </div>

            <div className="navbar-action-group">
              <button
                type="button"
                className="nav-link navbar-help-btn"
                onClick={handleHelpClick}
              >
                Help
              </button>
              {helpPromptVisible && (
                <div className="navbar-help-prompt">Starting tutorial…</div>
              )}

              <div className="desktop-a11y-wrapper">
                <AccessibilityMenu />
              </div>
            </div>

            {/* Trust dropdown */}
            <div
              className={`dropdown ${trustDropOpen ? 'dropdown-open' : ''}`}
              ref={trustDropRef}
            >
              <Link
                to="/trust"
                className="nav-link dropdown-toggle"
                onClick={(e) => {
                  if (window.innerWidth <= 1024) {
                    e.preventDefault();
                    setTrustDropOpen((prev) => !prev);
                    setSocialDropOpen(false);
                  }
                }}
                aria-expanded={trustDropOpen}
                aria-haspopup="menu"
              >
                <span>Trust</span>
                <span className="dropdown-arrow-indicator">{trustDropOpen ? '▲' : '▼'}</span>
              </Link>
              <div className={`dropdown-menu ${trustDropOpen ? 'show' : ''}`}>
                <Link to="/trust"        className="dropdown-item dropdown-item--overview" onClick={() => { setTrustDropOpen(false); closeMenu(); }}>Trust Overview</Link>
                <Link to="/about"        className="dropdown-item" onClick={() => { setTrustDropOpen(false); closeMenu(); }}>About Us</Link>
                <Link to="/trustees"     className="dropdown-item" onClick={() => { setTrustDropOpen(false); closeMenu(); }}>Meet the Trustees</Link>
                <Link to="/trust#donate" className="dropdown-item" onClick={() => { setTrustDropOpen(false); closeMenu(); }}>Donate</Link>
                <Link to="/history"      className="dropdown-item" onClick={() => { setTrustDropOpen(false); closeMenu(); }}>History</Link>
                <Link to="/community"    className="dropdown-item" onClick={() => { setTrustDropOpen(false); closeMenu(); }}>Community</Link>
              </div>
            </div>

            {/* Social Club */}
            <div
              className={`dropdown ${socialDropOpen ? 'dropdown-open' : ''}`}
              ref={socialDropRef}
            >
              <Link
                to="/social"
                className="nav-link dropdown-toggle"
                onClick={(e) => {
                  if (window.innerWidth <= 1024) {
                    e.preventDefault();
                    setSocialDropOpen((prev) => !prev);
                    setTrustDropOpen(false);
                  }
                }}
                aria-expanded={socialDropOpen}
                aria-haspopup="menu"
              >
                <span>Social Club</span>
                <span className="dropdown-arrow-indicator">{socialDropOpen ? '▲' : '▼'}</span>
              </Link>
              <div className={`dropdown-menu ${socialDropOpen ? 'show' : ''}`}>
                <Link to="/social"         className="dropdown-item dropdown-item--overview" onClick={() => { setSocialDropOpen(false); closeMenu(); }}>Social Club Home</Link>
                <Link to="/social/booking" className="dropdown-item" onClick={() => { setSocialDropOpen(false); closeMenu(); }}>Function Suite Hire</Link>
                <Link to="/trustees"       className="dropdown-item" onClick={() => { setSocialDropOpen(false); closeMenu(); }}>Trustees & Management</Link>
                <Link to="/contact#social" className="dropdown-item" onClick={() => { setSocialDropOpen(false); closeMenu(); }}>Social Enquiries & Hours</Link>
              </div>
            </div>

            {/* Direct Contact link for mobile drawer */}
            <Link
              to="/contact"
              className="nav-link mobile-drawer-contact-link"
              onClick={closeMenu}
            >
              Contact Us
            </Link>

            {/* ── Auth button ── */}
            {user ? (
              <div
                className={`dropdown account-dropdown ${accountDropOpen ? 'account-open' : ''}`}
                ref={accountDropRef}
              >
                <button
                  className="btn btn-outline dropdown-toggle"
                  style={styles.accountBtn}
                  onClick={() => {
                    navigate(isAdmin ? '/admin-account' : '/account');
                    setAccountDropOpen(!accountDropOpen);
                  }}
                  aria-expanded={accountDropOpen}
                  aria-haspopup="menu"
                >
                  <span style={styles.avatarDot}>
                    {user.email.charAt(0).toUpperCase()}
                  </span>
                  {accountLabel}
                </button>

                <div className={`dropdown-menu account-dropdown-menu ${accountDropOpen ? 'account-menu-open' : ''}`}>

                  {/* Admin-only links */}
                  {isAdmin && (
                    <>
                      <Link
                        to="/admin-account"
                        className="dropdown-item"
                        style={styles.adminItem}
                        onClick={() => { setAccountDropOpen(false); closeMenu(); }}
                      >
                        <i className="icon-cog" style={styles.actionIcon} />
                        Admin Dashboard
                      </Link>
                      <Link
                        to="/admin/events"
                        className="dropdown-item"
                        style={styles.adminItem}
                        onClick={() => { setAccountDropOpen(false); closeMenu(); }}
                      >
                        <i className="icon-calendar" style={styles.actionIcon} />
                        Manage Events
                      </Link>
                      <Link
                        to="/admin/social-staff"
                        className="dropdown-item"
                        style={styles.adminItem}
                        onClick={() => { setAccountDropOpen(false); closeMenu(); }}
                      >
                        <i className="icon-calendar" style={styles.actionIcon} />
                        Manage Social Staff
                      </Link>
                      <Link
                        to="/admin/create-account"
                        className="dropdown-item"
                        style={styles.adminItem}
                        onClick={() => { setAccountDropOpen(false); closeMenu(); }}
                      >
                        <i className="icon-calendar" style={styles.actionIcon} />
                        Create Admin Accounts
                      </Link>
                      <div style={styles.dropdownDivider} />
                    </>
                  )}

                  {/* Regular account link — always shown */}
                  <Link
                    to="/account"
                    className="dropdown-item"
                    onClick={() => { setAccountDropOpen(false); closeMenu(); }}
                  >
                    <i className="icon-adult" style={styles.actionIcon} />
                    My Account
                  </Link>

                  <div style={styles.dropdownDivider} />

                  <button
                    className="dropdown-item"
                    onClick={handleSignOut}
                    style={styles.signOutItem}
                  >
                    <i className="icon-left" style={styles.actionIcon} />
                    Sign Out
                  </button>

                </div>
              </div>
            ) : (
              <Link id="nav-login" to="/login" className="btn btn-outline" onClick={closeMenu}>
                Login
              </Link>
            )}

          </div>
        </div>

      </div>


      <LogoutConfirmModal
        isOpen={Boolean(logoutModalStep)}
        step={logoutModalStep}
        userEmail={user?.email || ''}
        isSubmitting={logoutSubmitting}
        onCancel={() => {
          if (!logoutSubmitting) setLogoutModalStep(null);
        }}
        onContinue={() => setLogoutModalStep('save-details')}
        onSaveAndLogout={() => performSignOut(true)}
        onLogoutWithoutSaving={() => performSignOut(false)}
      />
    </nav>
  );
};

const styles = {
  accountBtn: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  avatarDot: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    backgroundColor: '#1e3f32',
    color: '#ffffff',
    fontSize: '12px',
    fontWeight: '700',
    marginRight: '7px',
  },
  adminItem: {
    color: '#1e3f32',
    fontWeight: '600',
  },
  actionIcon: {
    display: 'inline-block',
    fontSize: '1.05em',
    marginRight: '8px',
    verticalAlign: 'middle',
  },
  dropdownDivider: {
    borderTop: '1px solid #f3f4f6',
    margin: '6px 0',
  },
  signOutItem: {
    width: '100%',
    textAlign: 'left',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#dc2626',
    padding: '12px 24px',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    fontWeight: '500',
  },
};

export default Navbar;